import { IBraveSearchSetting, ICommonSettings } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigBraveSearchSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IBraveSearchSetting;
}

export interface IConfigBraveSearchSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingBraveSearchId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigBraveSearchSettings extends React.PureComponent<
  IConfigBraveSearchSettingsProps,
  IConfigBraveSearchSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigBraveSearchSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigBraveSearchSettingsProps & IWithSDProps) {
    if (prevProps.editingBraveSearchId !== this.props.editingBraveSearchId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingBraveSearchId) return;

    const editingConfig = this.props.commonSettings.braveSearchSettings?.find(
      (x) => x.id === this.props.editingBraveSearchId
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const braveSearchSetting = this.props.commonSettings?.braveSearchSettings;
    if (!braveSearchSetting) return;

    const isSettingAlreadyExist = braveSearchSetting.some(
      (x) => x.name === value && x.id !== this.props.editingBraveSearchId
    );

    if (isSettingAlreadyExist) throw new Error("Brave Search Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IBraveSearchSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      apiKey: data.apiKey
    };

    if (this.props.editingBraveSearchId) {
      obj.id = this.props.editingBraveSearchId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "braveSearchSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "braveSearchSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingBraveSearchId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingBraveSearchId || "",
      configKey: "braveSearchSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Brave Search setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Brave Search setting"
        secondHeader="Brave Search setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingBraveSearchId ? this.deleteSetting : undefined}
            deleteTestId="mcpBraveSearchSettingDeleteButton"
            submitTestId="mcpBraveSearchSettingCreateButton"
            submitLabel={this.props.editingBraveSearchId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configBraveSearchForm"
          />
        }
      >
        <Form
          id="configBraveSearchForm"
          name="braveSearchSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("mcp_bravesearch_description")}>
            <ConfigField label="Name" required tooltipContentKey="mcp_bravesearch_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Brave Search Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Brave Search Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="mcpBraveSearchSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="API Key" required tooltipContentKey="mcp_bravesearch_apikey">
              <Form.Item name="apiKey" rules={[{ required: true, message: "Please enter a Brave Search API Key." }]}>
                <KuikaInput size="md" data-testid="mcpBraveSearchSettingApiKeyInput" autoFocus />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
